/**
 * Environment variable validation utility
 * Ensures all required environment variables are set at startup
 */

interface EnvConfig {
  // Frontend environment variables
  REACT_APP_API_URL: string;
  NODE_ENV: string;
  
  // Optional frontend variables
  REACT_APP_DEBUG?: string;
  REACT_APP_VERSION?: string;
}

const requiredEnvVars: (keyof EnvConfig)[] = [
  'NODE_ENV'
];

const optionalEnvVars: (keyof EnvConfig)[] = [
  'REACT_APP_API_URL',
  'REACT_APP_DEBUG',
  'REACT_APP_VERSION'
];

export function validateEnvironmentVariables(): {
  isValid: boolean;
  missing: string[];
  warnings: string[];
} {
  const missing: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  for (const envVar of requiredEnvVars) {
    if (!process.env[envVar]) {
      missing.push(envVar);
    }
  }

  // Check optional variables and provide warnings
  for (const envVar of optionalEnvVars) {
    if (!process.env[envVar]) {
      warnings.push(`Optional environment variable ${envVar} is not set`);
    }
  }

  // Validate specific values
  if (process.env.NODE_ENV && !['development', 'production', 'test'].includes(process.env.NODE_ENV)) {
    warnings.push(`NODE_ENV should be 'development', 'production', or 'test', got: ${process.env.NODE_ENV}`);
  }

  if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.startsWith('http')) {
    warnings.push('REACT_APP_API_URL should be a valid URL starting with http:// or https://');
  }

  return {
    isValid: missing.length === 0,
    missing,
    warnings
  };
}

export function logEnvironmentStatus(): void {
  const validation = validateEnvironmentVariables();
  const debugEnabled = process.env.REACT_APP_DEBUG === 'true';
  
  if (!validation.isValid) {
    console.error('❌ Missing required environment variables:', validation.missing);
    console.error('Please set the following environment variables:');
    validation.missing.forEach(envVar => {
      console.error(`  - ${envVar}`);
    });
    return;
  }

  if (debugEnabled && validation.warnings.length > 0) {
    console.warn('⚠️ Environment variable warnings:');
    validation.warnings.forEach(warning => {
      console.warn(`  - ${warning}`);
    });
  }

  // Keep development console clean unless there are actionable warnings/errors.
}

// Auto-validate on module load
if (typeof window !== 'undefined') {
  logEnvironmentStatus();
}
